import { Scale } from '@/services/canvas/Scale';
import useColorUtils from '@/utils/colorUtils';
import { EdgeTemplate, getEdgeType } from '@/modules/Edge';
import type { EdgeTypeValue, EdgeRoutingContext } from '../types/EdgeTypes';
import { calculateBoundsForEdge } from '../utils/boundsCalculator';
import { calculateNodesCollidingForEdge } from '../utils/collidesCalculator';
import { getMapAreaService } from '@/modules/map-editor/composables/useMapArea';

const { hexToRGB } = useColorUtils();

interface Point {
  x: number;
  y: number;
}

interface NullablePoint {
  x: number | null;
  y: number | null;
}

interface EdgeConfig {
  id: number;
  template: string | null;
  routingType?: EdgeTypeValue;
  startPosX: number;
  startPosY: number;
  endPosX: number;
  endPosY: number;
  fromNodeId: number;
  fromNodeHeight: number;
  fromNodeWidth: number;
  fromNodeBorderWidth: number;
  fromNodeBorder: boolean;
  fromNodeShape: string;
  lineStart: string;
  lineStartSize: string | number;
  toNodeId: number;
  toNodeHeight: number;
  toNodeWidth: number;
  toNodeBorderWidth: number;
  toNodeBorder: boolean;
  toNodeShape: string;
  lineEnd: string;
  lineEndSize: string | number;
  border: boolean;
  borderColor: string;
  borderStyle: string;
  borderWidth: number;
  fontColor: string;
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  textPosition: string;
  textDecoration: string;
  shadow: boolean;
  shadowColor: string;
  shadowX: number;
  shadowY: number;
  shadowBlur: number;
  contentStart: string;
  contentMiddle: string | null;
  contentEnd: string;
  content: { start: string | null; middle: string | null; end: string | null } | null;
  visibilityMin: number;
  visibilityMax: number;
  editing: boolean;
  hidden: boolean;
}

interface EdgeDrawContexts {
  ctxBuffered: CanvasRenderingContext2D;
  ctxHighlights: CanvasRenderingContext2D;
}

interface EdgeDrawArea {
  width: number | null;
  height: number | null;
  shiftX: number;
  shiftY: number;
}

interface Mouse {
  x: number;
  y: number;
}

export type { EdgeConfig, EdgeDrawContexts, EdgeDrawArea };

export default class Edge extends EdgeTemplate {
  template: string | null;

  fromNodeId: number;
  fromNodeWidth: number;
  fromNodeHeight: number;
  fromNodeBorderWidthValue: number;
  fromNodeBorder: boolean;
  fromNodeShape: string;
  fromPoint: Point;

  toNodeId: number;
  toNodeWidth: number;
  toNodeHeight: number;
  toNodeBorderWidthValue: number;
  toNodeBorder: boolean;
  toNodeShape: string;
  toPoint: Point;

  middlePoint: NullablePoint;

  textDecoration: string;

  declare contentStart: string;
  declare contentEnd: string;
  declare content: { start: string | null; middle: string | null; end: string | null } | null;

  ctxPath: CanvasRenderingContext2D | null;
  globalAlpha: number;

  _HOVER_COLOR: string;
  _LOCKED_COLOR: string;

  constructor({
    id, template, routingType, startPosX, startPosY, endPosX, endPosY,
    fromNodeId, fromNodeHeight, fromNodeWidth, fromNodeBorderWidth, fromNodeBorder, fromNodeShape, lineStart, lineStartSize,
    toNodeId, toNodeHeight, toNodeWidth, toNodeBorderWidth, toNodeBorder, toNodeShape, lineEnd, lineEndSize,
    border, borderColor, borderStyle, borderWidth, fontColor, fontSize, fontFamily, fontWeight, fontStyle,
    textPosition, textDecoration,
    shadow, shadowColor, shadowX, shadowY, shadowBlur,
    contentStart, contentMiddle, contentEnd, content,
    visibilityMin, visibilityMax,
    editing, hidden
  }: EdgeConfig) {
    super({
      id, type: '', name: '', routingType: routingType ?? 'straight',
      lineStart, lineStartSize, lineEnd, lineEndSize,
      border, borderColor, borderWidth, borderStyle,
      fontColor, fontSize, fontFamily, fontWeight, fontStyle, textPosition,
      shadow, shadowColor, shadowX, shadowY, shadowBlur,
      contentMiddle
    });
    this.template = template;

    this.fromNodeId = fromNodeId;
    this.fromNodeWidth = fromNodeWidth;
    this.fromNodeHeight = fromNodeHeight;
    this.fromNodeBorderWidthValue = fromNodeBorder ? fromNodeBorderWidth : 0;
    this.fromNodeBorder = fromNodeBorder;
    this.fromNodeShape = fromNodeShape;
    this.fromPoint = { x: 0, y: 0 };

    this.toNodeId = toNodeId;
    this.toNodeWidth = toNodeWidth;
    this.toNodeHeight = toNodeHeight;
    this.toNodeBorderWidthValue = toNodeBorder ? toNodeBorderWidth : 0;
    this.toNodeBorder = toNodeBorder;
    this.toNodeShape = toNodeShape;
    this.toPoint = { x: 0, y: 0 };

    this.startPosX = startPosX;
    this.endPosX = endPosX;
    this.startPosY = startPosY;
    this.endPosY = endPosY;
    this.middlePoint = { x: null, y: null };

    this.textDecoration = textDecoration;

    this.contentStart = contentStart;
    this.contentEnd = contentEnd;
    this.content = content;

    this.visibilityMin = visibilityMin;
    this.visibilityMax = visibilityMax;

    this.hidden = hidden;
    this.editing = editing;

    this.ZOOM_MAX = 1.62854;
    this._HOVER_COLOR = '#B9E0E3';
    this._LOCKED_COLOR = '#ED6F78';
    this._LABEL_PADDING = 10;

    this.ctxPath = null;
    this.globalAlpha = 1;
  }

  set fromNodeBorderWidth(val: number) {
    this.fromNodeBorderWidthValue =
      this.fromNodeBorder ?
        val :
        0;
  }

  get fromNodeBorderWidth(): number {
    return this.fromNodeBorderWidthValue;
  }

  set toNodeBorderWidth(val: number) {
    this.toNodeBorderWidthValue =
      this.toNodeBorder ?
        val :
        0;
  }

  get toNodeBorderWidth(): number {
    return this.toNodeBorderWidthValue;
  }

  cache(layerID: number, ctxPath?: CanvasRenderingContext2D | null): void {
    this.ctxPath = ctxPath || this.ctxPath || null;
    this.currentLayerId = layerID || this.currentLayerId || 1;
    this.globalAlpha = super._getVisibility(layerID);
    // set common alpha and calc color
    this._setCurrentColor();

    // if nodes colliding, edge shouldn't be drawn
    const isNodesColliding = calculateNodesCollidingForEdge(
      this,
      { x: this.startPosX, y: this.startPosY, radius: this.fromNodeWidth / 2 + this.fromNodeBorderWidth },
      { x: this.endPosX, y: this.endPosY, radius: this.toNodeWidth / 2 + this.toNodeBorderWidth }
    );
    if (isNodesColliding) return;

    // World-space edges draw directly in draw() — recalculate connection points
    if (super._isWorldSpace()) {
      calculateBoundsForEdge(this);
      return;
    }

    // get coords of the computed edge's start point which includes shadow and arrow shifts if any...
    ({ x: this.xCache, y: this.yCache } = super._getEdgeCoordinates());

    // draw path on cache canvas
    super._drawEdge();
    super._drawArrows();
    super._drawText();
  }

  cacheUpdate(layerID: number, ctxPath?: CanvasRenderingContext2D | null): void {
    this.cacheClear();
    this.cache(layerID, ctxPath);
  }

  draw(
    contexts: EdgeDrawContexts,
    zoomLevel: number,
    selectedID: number,
    ignoreMapArea: boolean = false,
    area: EdgeDrawArea = { width: null, height: null, shiftX: 0, shiftY: 0 }
  ): void {
    if (this.hidden) return;

    const { ctxBuffered: ctx, ctxHighlights } = contexts;
    if (selectedID > -1 && selectedID === this.id) {
      this.setSelected(ctxHighlights, selectedID, zoomLevel);
    }

    ctx.save();

    const mapAreaService = getMapAreaService();
    const x = mapAreaService.x;
    const y = mapAreaService.y;

    const { width, height, shiftX, shiftY } = area;
    const shiftMapX = ignoreMapArea ? width! / 2 + Math.abs(shiftX) : x;
    const shiftMapY = ignoreMapArea ? height! / 2 + Math.abs(shiftY) : y;

    if (super._isWorldSpace()) {
      // World-space: draw directly on the main canvas
      const scaledFromX = Scale.x(this.fromPoint.x + shiftMapX, ctx.canvas.width / 2, zoomLevel);
      const scaledFromY = Scale.y(this.fromPoint.y + shiftMapY, ctx.canvas.height / 2, zoomLevel);
      const scaledToX = Scale.x(this.toPoint.x + shiftMapX, ctx.canvas.width / 2, zoomLevel);
      const scaledToY = Scale.y(this.toPoint.y + shiftMapY, ctx.canvas.height / 2, zoomLevel);

      // Build routing context with scaled node centers and dimensions
      const scaledFromCenterX = Scale.x(this.startPosX + shiftMapX, ctx.canvas.width / 2, zoomLevel);
      const scaledFromCenterY = Scale.y(this.startPosY + shiftMapY, ctx.canvas.height / 2, zoomLevel);
      const scaledToCenterX = Scale.x(this.endPosX + shiftMapX, ctx.canvas.width / 2, zoomLevel);
      const scaledToCenterY = Scale.y(this.endPosY + shiftMapY, ctx.canvas.height / 2, zoomLevel);

      const routingCtx: EdgeRoutingContext = {
        fromCenterX: scaledFromCenterX, fromCenterY: scaledFromCenterY,
        toCenterX: scaledToCenterX, toCenterY: scaledToCenterY,
        fromHalfW: (this.fromNodeWidth / 2 + this.fromNodeBorderWidthValue) * zoomLevel,
        fromHalfH: (this.fromNodeHeight / 2 + this.fromNodeBorderWidthValue) * zoomLevel,
        toHalfW: (this.toNodeWidth / 2 + this.toNodeBorderWidthValue) * zoomLevel,
        toHalfH: (this.toNodeHeight / 2 + this.toNodeBorderWidthValue) * zoomLevel,
      };

      // Scale line width and arrow sizes with zoom
      const savedBorderWidth = this.borderWidth;
      this.borderWidth = this.borderWidth * zoomLevel;

      const result = super._drawWorldEdge(ctx, scaledFromX, scaledFromY, scaledToX, scaledToY, routingCtx);
      super._drawWorldArrows(ctx, scaledFromX, scaledFromY, scaledToX, scaledToY, result?.startTangent, result?.endTangent);
      super._drawWorldText(ctx, scaledFromX, scaledFromY, scaledToX, scaledToY);

      this.borderWidth = savedBorderWidth;

      if (result) this.pathInteraction = result.path;
    } else {
      // Cache-based: rotate and draw cached bitmap
      const edgeAngle = Math.atan2((this.endPosY - this.startPosY), (this.endPosX - this.startPosX));

      const computedRotateX = Scale.x(this.fromPoint.x + shiftMapX, ctx.canvas.width / 2, zoomLevel);
      const computedRotateY = Scale.y(this.fromPoint.y + shiftMapY, ctx.canvas.height / 2, zoomLevel);

      const computedX = Scale.x(this.fromPoint.x - this.xCache! + shiftMapX, ctx.canvas.width / 2, zoomLevel);
      const computedY = Scale.y(this.fromPoint.y - this.yCache! + shiftMapY, ctx.canvas.height / 2, zoomLevel);
      const computedInteractX = Scale.x(this.fromPoint.x + x, ctx.canvas.width / 2, zoomLevel);
      const computedInteractY = Scale.y(this.fromPoint.y - this._HOVER_AREA / 2 - this.borderWidth / 2 + y, ctx.canvas.height / 2, zoomLevel);

      const ZOOM_FACTOR = this.ZOOM_MAX * this.pixelRatio!;

      if (!this.cacheInstance || !this.cacheInstance.width || !this.cacheInstance.height) return;

      super._ctxRotate(edgeAngle, computedRotateX, computedRotateY, ctx);

      ctx.drawImage(this.cacheInstance!, computedX, computedY, (this.cacheInstance!.width / ZOOM_FACTOR) * zoomLevel, (this.cacheInstance!.height / ZOOM_FACTOR) * zoomLevel);

      this.pathInteraction = new Path2D();
      this.pathInteraction.rect(
        computedInteractX,
        computedInteractY,
        this.edgeBounds!.width * zoomLevel,
        (this.borderWidth + this._HOVER_AREA) * zoomLevel
      );
    }

    ctx.restore();
  }

  setSelected(ctx: CanvasRenderingContext2D, selectedId: number, zoomLevel: number): void {
    this._getMiddlePoint(); // get coords of edge's center
    if (this.id === selectedId) this._selectedMark(ctx, zoomLevel);
  }

  checkHovered(ctx: CanvasRenderingContext2D, mouse: Mouse, zoomLevel: number): boolean | null {
    if (!this.pathInteraction) return null;

    if (super._isWorldSpace()) {
      // World-space: test stroke directly on the Path2D, no rotation needed
      ctx.save();
      ctx.lineWidth = (this._HOVER_AREA + this.borderWidth) * zoomLevel;
      this.hover = !this.hidden ? ctx.isPointInStroke(this.pathInteraction, mouse.x, mouse.y) : false;
      ctx.restore();
    } else {
      // Cache-based: rotate context before testing the rectangular path
      const mapAreaService = getMapAreaService();
      const x = mapAreaService.x;
      const y = mapAreaService.y;
      const edgeAngle = Math.atan2((this.endPosY - this.startPosY), (this.endPosX - this.startPosX));
      const computedRotateX = Scale.x(this.fromPoint.x + x, ctx.canvas.width / 2, zoomLevel);
      const computedRotateY = Scale.y(this.fromPoint.y + y, ctx.canvas.height / 2, zoomLevel);

      ctx.save();
      this._ctxRotate(edgeAngle, computedRotateX, computedRotateY, ctx);
      ctx.lineWidth = this._HOVER_AREA + this.borderWidth;
      this.hover = !this.hidden ? ctx.isPointInPath(this.pathInteraction, mouse.x, mouse.y) : false;
      ctx.restore();
    }

    return this.hover;
  }

  /**
   * Resolve the colour this edge is stroked and labelled with.
   *
   * The result is baked into the cached bitmap, and that cache is only rebuilt
   * when the edge's own data changes — so only persistent edge state may be
   * read here. Pointer hover is owned by `HoverDetectionService`, which flips
   * it on every pointer move without invalidating any cache, and so must not
   * influence the cached colour.
   */
  private _setCurrentColor(): void {
    if (!this.locked) {
      this._currentColor = this.borderColor;
      return;
    }

    const alpha = this.ctx ? this.ctx.globalAlpha : 1;
    const { r, g, b } = hexToRGB(this._LOCKED_COLOR);
    this._currentColor = `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private _getMiddlePoint(): void {
    if (super._isWorldSpace()) {
      const edgeType = getEdgeType(this.routingType);
      if (edgeType.getTextAnchor) {
        // Apply arrow offsets to match the actual drawn path
        const centerAngle = Math.atan2(
          this.endPosY - this.startPosY, this.endPosX - this.startPosX
        );
        const startAngle = super._snapToAxis(centerAngle);
        const endAngle = super._snapToAxis(centerAngle + Math.PI) + Math.PI;
        const { startOffset, endOffset } = super._getArrowOffsets();
        const adjFromX = this.fromPoint.x + Math.cos(startAngle) * startOffset;
        const adjFromY = this.fromPoint.y + Math.sin(startAngle) * startOffset;
        const adjToX = this.toPoint.x - Math.cos(endAngle) * endOffset;
        const adjToY = this.toPoint.y - Math.sin(endAngle) * endOffset;

        const anchor = edgeType.getTextAnchor(adjFromX, adjFromY, adjToX, adjToY);
        this.middlePoint.x = anchor.x;
        this.middlePoint.y = anchor.y;
        return;
      }
    }
    this.middlePoint.x = (this.fromPoint.x + this.toPoint.x) / 2;
    this.middlePoint.y = (this.fromPoint.y + this.toPoint.y) / 2;
  }

  private _selectedMark(ctx: CanvasRenderingContext2D, zoomLevel: number): void {
    const dotRadius = 4;
    const mapAreaService = getMapAreaService();
    const x = mapAreaService.x;
    const y = mapAreaService.y;

    ctx.save();
    // draw middle point
    const middlePoint = {
      ctx,
      x: Scale.x(this.middlePoint.x! + x, ctx.canvas.width / 2, zoomLevel),
      y: Scale.y(this.middlePoint.y! + y, ctx.canvas.height / 2, zoomLevel),
      radius: dotRadius * zoomLevel,
      startAngle: 0,
      endAngle: Math.PI * 2,
      couterclockwise: false,
      fillStyle: '#0125ea',
      strokeStyle: '#ffffff',
      lineWidth: zoomLevel
    };
    const fromPoint = {
      ctx,
      x: Scale.x(this.fromPoint.x + x, ctx.canvas.width / 2, zoomLevel),
      y: Scale.y(this.fromPoint.y + y, ctx.canvas.height / 2, zoomLevel),
      radius: dotRadius * zoomLevel,
      startAngle: 0,
      endAngle: Math.PI * 2,
      couterclockwise: false,
      fillStyle: '#ffffff',
      strokeStyle: '#0125ea',
      lineWidth: zoomLevel
    };
    const toPoint = {
      ctx,
      x: Scale.x(this.toPoint.x + x, ctx.canvas.width / 2, zoomLevel),
      y: Scale.y(this.toPoint.y + y, ctx.canvas.height / 2, zoomLevel),
      radius: dotRadius * zoomLevel,
      startAngle: 0,
      endAngle: Math.PI * 2,
      couterclockwise: false,
      fillStyle: '#ffffff',
      strokeStyle: '#0125ea',
      lineWidth: zoomLevel
    };

    this._drawCircle(middlePoint);
    this._drawCircle(fromPoint);
    this._drawCircle(toPoint);

    ctx.closePath();
    ctx.restore();
  }
}
